const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export class ApiError extends Error {
  constructor(status, detail) {
    super(`API ${status}: ${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function readError(res) {
  const text = await res.text().catch(() => '');
  try {
    const j = JSON.parse(text);
    return j.error || text;
  } catch {
    return text || res.statusText;
  }
}

/**
 * Call the Flightdeck backend. Auth is the session cookie (credentials: 'include').
 * On 401 the app falls back to the sign-in screen via the 'fd-unauthorized' event.
 */
export async function api(path, { method = 'GET', body, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    signal,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    if (!path.startsWith('/auth/') && !path.startsWith('/portal') && !path.startsWith('/move') && !path.startsWith('/public')) window.dispatchEvent(new Event('fd-unauthorized'));
    throw new ApiError(401, await readError(res));
  }
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return null;
  return res.json();
}

/** POST multipart form data (e.g. a file upload) with the session cookie. */
export async function apiUpload(path, formData, { method = 'POST', signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method, signal, credentials: 'include',
    headers: { Accept: 'application/json' }, // let the browser set the multipart boundary
    body: formData,
  });
  if (res.status === 401) {
    if (!path.startsWith('/auth/') && !path.startsWith('/portal') && !path.startsWith('/move') && !path.startsWith('/public')) window.dispatchEvent(new Event('fd-unauthorized'));
    throw new ApiError(401, await readError(res));
  }
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  if (res.status === 204) return null;
  return res.json();
}

/** Fetch binary content (e.g. a PDF) with the session cookie; returns a Blob. */
export async function apiBlob(path, { signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', signal });
  if (res.status === 401) {
    if (!path.startsWith('/auth/') && !path.startsWith('/portal') && !path.startsWith('/move') && !path.startsWith('/public')) window.dispatchEvent(new Event('fd-unauthorized'));
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return res.blob();
}
